/**
 * OfflineSyncStatusPage
 * Shows all offline verification sessions with their sync status.
 * Accessible from the verifier workspace at /offline-sync.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import {
  Sync,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  CloudDone,
  CloudOff,
  Refresh,
  CameraAlt,
  Mic,
  GpsFixed,
  WifiOff
} from '@mui/icons-material';
import indexedDBHelper from '../utils/indexedDBHelper';
import offlineSyncService from '../services/offlineSyncService';
import { deleteSession } from '../services/offlineVerificationSession';

const STATUS_CONFIG = {
  preflight: { label: 'Preparing', color: 'default', icon: <HourglassEmpty /> },
  ready: { label: 'Ready', color: 'info', icon: <CloudOff /> },
  in_progress: { label: 'In Progress', color: 'warning', icon: <HourglassEmpty /> },
  evidence_complete: { label: 'Evidence Done', color: 'info', icon: <CheckCircle /> },
  signed: { label: 'Signed', color: 'success', icon: <CheckCircle /> },
  syncing: { label: 'Syncing...', color: 'info', icon: <Sync /> },
  synced: { label: 'Synced', color: 'success', icon: <CloudDone /> },
  sync_failed: { label: 'Sync Failed', color: 'error', icon: <ErrorIcon /> }
};

const OfflineSyncStatusPage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const loadSessions = useCallback(async () => {
    try {
      const all = await indexedDBHelper.getAllSessions();
      setSessions(all.sort((a, b) => (b.startedAt || b.preflight?.cachedAt || 0) > (a.startedAt || a.preflight?.cachedAt || 0) ? 1 : -1));
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [loadSessions]);

  // Listen to sync events
  useEffect(() => {
    const unsub = offlineSyncService.onStatusChange((event) => {
      if (event.type === 'sync_started') setSyncing(true);
      if (event.type === 'sync_completed' || event.type === 'session_sync_failed') {
        setSyncing(false);
        loadSessions();
      }
    });
    return unsub;
  }, [loadSessions]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      await offlineSyncService.syncAllPending();
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
      loadSessions();
    }
  };

  const handleSyncOne = async (sessionId) => {
    setSyncing(true);
    try {
      await offlineSyncService.syncSession(sessionId);
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
      loadSessions();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSession(deleteTarget);
      setDeleteTarget(null);
      loadSessions();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const pendingSessions = sessions.filter(s => s.status === 'signed' || s.status === 'sync_failed');

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Offline Verifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage verification sessions completed offline. Sync when you have connectivity.
      </Typography>

      {!isOnline && (
        <Alert severity="warning" icon={<WifiOff />} sx={{ mb: 2 }}>
          You are offline. Sessions will sync automatically when connectivity returns.
        </Alert>
      )}

      {/* Sync All Button */}
      {pendingSessions.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={syncing ? <Sync sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} /> : <Sync />}
            onClick={handleSyncAll}
            disabled={!isOnline || syncing}
            fullWidth
            size="large"
          >
            {syncing ? 'Syncing...' : `Sync ${pendingSessions.length} Pending Verification${pendingSessions.length !== 1 ? 's' : ''}`}
          </Button>
          {syncing && <LinearProgress sx={{ mt: 1, borderRadius: 2 }} />}
        </Box>
      )}

      {loading ? (
        <LinearProgress />
      ) : sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CloudDone sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No offline sessions</Typography>
        </Paper>
      ) : (
        <List>
          {sessions.map((s) => {
            const config = STATUS_CONFIG[s.status] || STATUS_CONFIG.ready;
            const photoCount = s.photos?.length || 0;
            const voiceCount = s.voiceNotes?.length || 0;
            const proofCount = s.geofenceProofs?.length || 0;
            const verTitle = s.preflight?.verification?.title || `Verification #${s.verificationId}`;

            return (
              <React.Fragment key={s.sessionId}>
                <ListItem sx={{ px: 0 }}>
                  <Paper sx={{ p: 2, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {verTitle}
                      </Typography>
                      <Chip
                        icon={config.icon}
                        label={config.label}
                        color={config.color}
                        size="small"
                      />
                    </Box>

                    {/* Evidence counts */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip icon={<CameraAlt />} label={`${photoCount} photos`} size="small" variant="outlined" />
                      {voiceCount > 0 && <Chip icon={<Mic />} label={`${voiceCount} notes`} size="small" variant="outlined" />}
                      <Chip icon={<GpsFixed />} label={`${proofCount} GPS proofs`} size="small" variant="outlined" />
                    </Box>

                    {/* Timestamps */}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {s.startedAt ? `Started: ${new Date(s.startedAt).toLocaleString()}` : `Cached: ${new Date(s.preflight?.cachedAt).toLocaleString()}`}
                      {s.completedAt && ` | Completed: ${new Date(s.completedAt).toLocaleString()}`}
                    </Typography>

                    {/* Sync error */}
                    {s.status === 'sync_failed' && s.sync?.lastError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {s.sync.lastError}
                        {s.sync.attempts > 0 && ` (${s.sync.attempts} attempt${s.sync.attempts !== 1 ? 's' : ''})`}
                      </Alert>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                      {(s.status === 'signed' || s.status === 'sync_failed') && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Sync />}
                          onClick={() => handleSyncOne(s.sessionId)}
                          disabled={!isOnline || syncing}
                        >
                          Sync
                        </Button>
                      )}
                      {s.status !== 'syncing' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(s.sessionId)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button startIcon={<Refresh />} onClick={loadSessions} size="small">
          Refresh
        </Button>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Offline Session?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete the offline session and all associated photos and voice notes from your device.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfflineSyncStatusPage;
